import React from 'react';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-4">Terms and Conditions</h1>
            <p className="text-white/80 text-lg">D.C. Technology Corp. Marketplace Platform</p>
            <p className="text-white/60 text-sm mt-2">Last Updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="space-y-8 text-white/90 leading-relaxed">
            {/* Section 1: Acceptance of Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                1. Acceptance of Terms
              </h2>
              <p className="mb-4">
                By accessing, using, or registering for D.C. Technology Corp.'s marketplace platform ("Platform"), 
                you agree to be bound by these Terms and Conditions ("Terms"), our Privacy Policy, and all applicable 
                laws and regulations. If you do not agree with any of these terms, you are prohibited from using or 
                accessing this Platform.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you and D.C. Technology Corp., a corporation 
                organized under the laws of the United States ("Company," "we," "us," or "our").
              </p>
            </section>

            {/* Section 2: Modifications to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                2. Modifications and Updates
              </h2>
              <p className="mb-4">
                <strong>RIGHT TO MODIFY:</strong> We reserve the exclusive right to modify, update, revise, or 
                replace these Terms at any time, at our sole discretion, without prior notice. Modifications may 
                include but are not limited to changes in service features, pricing, policies, or operational procedures.
              </p>
              <p className="mb-4">
                <strong>NOTIFICATION:</strong> Material changes will be communicated through email notification to 
                your registered email address and/or prominent notice on the Platform. Minor changes may be posted 
                without individual notification.
              </p>
              <p>
                <strong>CONTINUED USE:</strong> Your continued use of the Platform after any modifications constitutes 
                acceptance of the revised Terms. If you disagree with any changes, your sole remedy is to discontinue 
                use of the Platform.
              </p>
            </section>

            {/* Section 3: Service Description */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                3. Service Description and Platform Purpose
              </h2>
              <p className="mb-4">
                D.C. Technology Corp. operates a multi-trade marketplace platform connecting customers seeking 
                professional services with qualified service providers across various industries including but not 
                limited to electrical, plumbing, HVAC, roofing, carpentry, engineering, real estate, and other 
                professional trades.
              </p>
              <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-blue-300 mb-2">Platform Services Include:</h3>
                <ul className="list-disc list-inside space-y-1 text-blue-200">
                  <li>Service provider registration and verification</li>
                  <li>Customer-provider matching and communication</li>
                  <li>Job posting and bidding system</li>
                  <li>Secure payment processing and escrow services</li>
                  <li>Review and rating system</li>
                  <li>Administrative oversight and compliance monitoring</li>
                </ul>
              </div>
            </section>

            {/* Section 4: User Registration and Eligibility */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                4. User Registration and Eligibility
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">4.1 Eligibility Requirements</h3>
                  <ul className="list-disc list-inside space-y-2 text-white/85">
                    <li>Must be at least 18 years of age</li>
                    <li>Must have legal capacity to enter into contracts</li>
                    <li>Must provide accurate and complete registration information</li>
                    <li>Service providers must possess valid licenses where required by law</li>
                    <li>Must comply with all applicable local, state, and federal regulations</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">4.2 Identity Verification</h3>
                  <p>
                    All users must complete identity verification by providing government-issued identification. 
                    This requirement is mandatory for platform security, regulatory compliance, and user protection. 
                    Identity documents are encrypted and stored securely in accordance with our Privacy Policy.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">4.3 Account Security</h3>
                  <p>
                    You are responsible for maintaining the confidentiality of your account credentials and for all 
                    activities that occur under your account. You agree to notify us immediately of any unauthorized 
                    use of your account.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5: Service Provider Obligations */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                5. Service Provider Obligations and Licensing
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">5.1 Professional Licensing</h3>
                  <p className="mb-2">
                    Service providers must maintain all required professional licenses, certifications, and permits 
                    for their respective trades. Licensed trades include but are not limited to:
                  </p>
                  <div className="bg-yellow-600/20 border border-yellow-400/30 rounded-lg p-4">
                    <p className="text-yellow-200 text-sm">
                      Electrical, Plumbing, HVAC, Roofing, Engineering disciplines, Real Estate services, 
                      and other regulated professions as defined by applicable jurisdictions.
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">5.2 Subscription Requirements</h3>
                  <p>
                    Service providers must maintain an active subscription to access platform features including 
                    job bidding, customer communication, and payment processing. Subscription plans range from 
                    Starter to Enterprise levels with varying features and capabilities.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">5.3 Quality Standards</h3>
                  <p>
                    Service providers agree to maintain professional standards, complete work in a timely manner, 
                    and provide quality services consistent with industry best practices and customer expectations.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 6: Payment Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                6. Payment Terms and Escrow Services
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">6.1 Platform Commission</h3>
                  <p>
                    The Platform charges a commission fee on completed transactions, currently set at 7% but subject 
                    to adjustment at the Company's discretion with appropriate notice to users.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">6.2 Escrow Services</h3>
                  <p className="mb-2">Payment protection is provided through our escrow system:</p>
                  <ul className="list-disc list-inside space-y-1 text-white/85">
                    <li><strong>Jobs under $500:</strong> 50% immediate payment, 50% held in escrow until completion</li>
                    <li><strong>Jobs $500 and above:</strong> Optional milestone-based payments available</li>
                    <li><strong>Release conditions:</strong> Customer confirmation or automatic release after 7 days of completion</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white/95 mb-2">6.3 No Refunds Policy</h3>
                  <div className="bg-red-600/20 border border-red-400/30 rounded-lg p-4">
                    <p className="text-red-200 font-semibold mb-2">ALL SALES ARE FINAL</p>
                    <p className="text-white/90">
                      There are no refunds for any reason. All subscription fees and platform charges are 
                      non-refundable. By completing a purchase on this platform, you acknowledge and agree 
                      that all transactions are final and non-refundable.
                    </p>
                    <p className="text-white/90 mt-3">
                      <strong>Dispute Disclaimer:</strong> D.C. Technology Corp. serves solely as a platform 
                      connecting customers with service providers. Any disputes, disagreements, or issues 
                      arising between customers and service providers are strictly between those parties. 
                      D.C. Technology Corp. is not responsible for resolving, mediating, or intervening in 
                      any customer-provider disputes and assumes no liability for the outcome of any such disputes.
                    </p>
                    <p className="text-white/80 mt-3 text-sm">
                      By using our services, you expressly waive any right to a refund, chargeback, or 
                      dispute of payment for any reason whatsoever, and acknowledge that D.C. Technology Corp. 
                      bears no responsibility for disputes between platform users.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 7: Prohibited Activities */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                7. Prohibited Activities and Conduct
              </h2>
              <div className="bg-red-600/20 border border-red-400/30 rounded-lg p-4">
                <p className="text-red-200 font-semibold mb-2">The following activities are strictly prohibited:</p>
                <ul className="list-disc list-inside space-y-1 text-red-200 text-sm">
                  <li>Fraudulent or misleading information</li>
                  <li>Circumventing platform fees or payment systems</li>
                  <li>Harassment, discrimination, or abusive behavior</li>
                  <li>Unlicensed provision of regulated services</li>
                  <li>Spam, unauthorized marketing, or solicitation</li>
                  <li>Violation of intellectual property rights</li>
                  <li>Any illegal activities or violation of applicable laws</li>
                </ul>
              </div>
            </section>

            {/* Section 8: Privacy and Data Protection */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                8. Privacy and Data Protection
              </h2>
              <p className="mb-4">
                Your privacy is important to us. Our collection, use, and protection of personal information is 
                governed by our Privacy Policy, which is incorporated by reference into these Terms. We employ 
                industry-standard encryption and security measures to protect sensitive data including:
              </p>
              <ul className="list-disc list-inside space-y-1 text-white/85">
                <li>AES-256 encryption for sensitive personal data</li>
                <li>Secure storage of government identification documents</li>
                <li>Payment information protection through certified processors</li>
                <li>Regular security audits and compliance monitoring</li>
              </ul>
            </section>

            {/* Section 9: Limitation of Liability and Comprehensive Disclaimers */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                9. COMPREHENSIVE LIMITATION OF LIABILITY AND DISCLAIMERS
              </h2>
              
              <div className="bg-red-900/30 border-2 border-red-400/50 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-red-300 mb-4">⚠️ IMPORTANT LEGAL NOTICE - READ CAREFULLY</h3>
                
                <div className="space-y-4 text-white/95">
                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.1 NO WARRANTIES - "AS IS" PLATFORM</h4>
                    <p className="text-sm leading-relaxed">
                      THE PLATFORM, ALL SERVICES, CONTENT, AND MATERIALS ARE PROVIDED STRICTLY ON AN "AS IS" AND 
                      "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST 
                      EXTENT PERMITTED BY APPLICABLE LAW, D.C. TECHNOLOGY CORP. AND ITS OFFICERS, DIRECTORS, EMPLOYEES, 
                      AGENTS, SUBSIDIARIES, AFFILIATES, SUCCESSORS, AND ASSIGNS (COLLECTIVELY "COMPANY PARTIES") EXPRESSLY 
                      DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT 
                      LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, 
                      NON-INFRINGEMENT, ACCURACY, COMPLETENESS, RELIABILITY, OR AVAILABILITY.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.2 PLATFORM AS INTERMEDIARY ONLY</h4>
                    <p className="text-sm leading-relaxed">
                      D.C. TECHNOLOGY CORP. ACTS SOLELY AS AN INTERMEDIARY PLATFORM CONNECTING SERVICE PROVIDERS WITH 
                      CUSTOMERS. WE DO NOT EMPLOY, RECOMMEND, ENDORSE, OR GUARANTEE ANY SERVICE PROVIDERS. WE ARE NOT 
                      PARTY TO ANY AGREEMENTS BETWEEN USERS. ALL TRANSACTIONS, SERVICES, CONTRACTS, AND INTERACTIONS 
                      BETWEEN CUSTOMERS AND SERVICE PROVIDERS ARE SOLELY BETWEEN THOSE PARTIES. WE MAKE NO REPRESENTATIONS 
                      OR WARRANTIES REGARDING THE QUALITY, SAFETY, LEGALITY, OR COMPLIANCE OF SERVICES PROVIDED.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.3 ZERO LIABILITY FOR USER CONDUCT</h4>
                    <p className="text-sm leading-relaxed">
                      COMPANY PARTIES SHALL HAVE ABSOLUTELY NO LIABILITY FOR ANY ACTIONS, CONDUCT, ERRORS, OMISSIONS, 
                      MISREPRESENTATIONS, OR NEGLIGENCE OF ANY PLATFORM USERS, INCLUDING BUT NOT LIMITED TO SERVICE 
                      PROVIDERS AND CUSTOMERS. THIS INCLUDES BUT IS NOT LIMITED TO: (A) QUALITY OR SAFETY OF SERVICES 
                      RENDERED; (B) PROPERTY DAMAGE; (C) PERSONAL INJURY OR DEATH; (D) FINANCIAL LOSSES; (E) DELAYS OR 
                      NON-PERFORMANCE; (F) BREACH OF CONTRACT; (G) FRAUD OR MISREPRESENTATION; (H) UNLICENSED OR 
                      UNQUALIFIED WORK; (I) CODE VIOLATIONS; (J) PROFESSIONAL NEGLIGENCE OR MALPRACTICE.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.4 MAXIMUM LIABILITY CAP</h4>
                    <p className="text-sm leading-relaxed">
                      TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE TOTAL AGGREGATE LIABILITY OF COMPANY PARTIES TO YOU 
                      FOR ANY AND ALL CLAIMS ARISING FROM OR RELATED TO YOUR USE OF THE PLATFORM, REGARDLESS OF THE FORM 
                      OF ACTION (WHETHER CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR OTHERWISE), SHALL NOT EXCEED 
                      THE LESSER OF: (A) THE TOTAL AMOUNT PAID BY YOU TO D.C. TECHNOLOGY CORP. FOR PLATFORM SUBSCRIPTION 
                      FEES IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO LIABILITY; OR (B) 
                      ONE HUNDRED DOLLARS ($100.00 USD). THIS LIMITATION APPLIES EVEN IF THE COMPANY HAS BEEN ADVISED OF 
                      THE POSSIBILITY OF SUCH DAMAGES.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.5 EXCLUSION OF DAMAGES</h4>
                    <p className="text-sm leading-relaxed">
                      IN NO EVENT SHALL COMPANY PARTIES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, 
                      EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO: (A) LOSS OF PROFITS, REVENUE, OR 
                      BUSINESS OPPORTUNITIES; (B) LOSS OF DATA OR INFORMATION; (C) COST OF SUBSTITUTE SERVICES; (D) LOSS 
                      OF GOODWILL OR REPUTATION; (E) BUSINESS INTERRUPTION; (F) PERSONAL INJURY; (G) PROPERTY DAMAGE; 
                      (H) EMOTIONAL DISTRESS; (I) ECONOMIC LOSSES; (J) ATTORNEY'S FEES OR LITIGATION COSTS, EVEN IF 
                      ADVISED OF THE POSSIBILITY OF SUCH DAMAGES AND REGARDLESS OF WHETHER SUCH DAMAGES WERE FORESEEABLE.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.6 NO LIABILITY FOR THIRD-PARTY ACTIONS</h4>
                    <p className="text-sm leading-relaxed">
                      COMPANY PARTIES SHALL HAVE NO LIABILITY WHATSOEVER FOR: (A) ACTIONS OR INACTIONS OF SERVICE PROVIDERS; 
                      (B) QUALITY, LEGALITY, OR SAFETY OF SERVICES PERFORMED; (C) ACCURACY OF PROVIDER CREDENTIALS OR 
                      QUALIFICATIONS; (D) DISPUTES BETWEEN USERS; (E) PAYMENT DISPUTES; (F) THIRD-PARTY PAYMENT PROCESSOR 
                      ISSUES; (G) IDENTITY THEFT OR FRAUD; (H) DATA BREACHES NOT CAUSED BY OUR GROSS NEGLIGENCE; (I) 
                      TECHNICAL FAILURES, SYSTEM DOWNTIME, OR PLATFORM ERRORS; (J) UNAUTHORIZED ACCESS TO USER ACCOUNTS.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.7 USER RESPONSIBILITY AND ASSUMPTION OF RISK</h4>
                    <p className="text-sm leading-relaxed">
                      BY USING THE PLATFORM, YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT: (A) YOU ASSUME ALL RISKS ASSOCIATED 
                      WITH HIRING SERVICE PROVIDERS THROUGH THE PLATFORM; (B) YOU ARE SOLELY RESPONSIBLE FOR VERIFYING 
                      PROVIDER CREDENTIALS, LICENSES, INSURANCE, AND QUALIFICATIONS; (C) YOU WILL CONDUCT YOUR OWN DUE 
                      DILIGENCE BEFORE ENTERING INTO ANY AGREEMENT; (D) YOU RELEASE COMPANY PARTIES FROM ALL LIABILITY 
                      RELATED TO PROVIDER SELECTION AND SERVICE QUALITY; (E) YOU UNDERSTAND THAT REVIEWS AND RATINGS MAY 
                      BE INACCURATE OR FRAUDULENT; (F) YOU AGREE TO HOLD COMPANY PARTIES HARMLESS FOR ALL LOSSES INCURRED.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.8 INDEMNIFICATION BY USER</h4>
                    <p className="text-sm leading-relaxed">
                      YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS COMPANY PARTIES FROM AND AGAINST ANY AND ALL 
                      CLAIMS, LIABILITIES, DAMAGES, LOSSES, COSTS, EXPENSES, FEES (INCLUDING REASONABLE ATTORNEYS' FEES 
                      AND COURT COSTS) ARISING FROM OR RELATING TO: (A) YOUR USE OR MISUSE OF THE PLATFORM; (B) YOUR 
                      VIOLATION OF THESE TERMS; (C) YOUR VIOLATION OF ANY LAW OR REGULATION; (D) YOUR VIOLATION OF ANY 
                      THIRD-PARTY RIGHTS; (E) ANY TRANSACTION OR INTERACTION WITH SERVICE PROVIDERS; (F) ANY CONTENT YOU 
                      SUBMIT; (G) ANY DISPUTES WITH OTHER USERS; (H) YOUR NEGLIGENCE OR WILLFUL MISCONDUCT.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.9 NO MONITORING OR VERIFICATION OBLIGATION</h4>
                    <p className="text-sm leading-relaxed">
                      D.C. TECHNOLOGY CORP. HAS NO OBLIGATION TO MONITOR, VERIFY, OR INVESTIGATE: (A) USER IDENTITIES; 
                      (B) PROVIDER CREDENTIALS OR LICENSES; (C) SERVICE QUALITY OR COMPLIANCE; (D) USER CONDUCT; (E) 
                      CONTENT ACCURACY; (F) TRANSACTION LEGITIMACY. ANY VERIFICATION SERVICES PROVIDED ARE FOR CONVENIENCE 
                      ONLY AND DO NOT CONSTITUTE AN ENDORSEMENT, GUARANTEE, OR WARRANTY. WE DISCLAIM ALL LIABILITY FOR 
                      VERIFICATION ERRORS OR OMISSIONS.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-red-300 mb-2">9.10 JURISDICTION-SPECIFIC LIMITATIONS</h4>
                    <p className="text-sm leading-relaxed">
                      SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR LIMITATIONS OF LIABILITY FOR 
                      INCIDENTAL OR CONSEQUENTIAL DAMAGES. IN SUCH JURISDICTIONS, THE LIABILITY OF COMPANY PARTIES SHALL 
                      BE LIMITED TO THE GREATEST EXTENT PERMITTED BY LAW. IF ANY PART OF THIS LIMITATION OF LIABILITY IS 
                      FOUND TO BE INVALID OR UNENFORCEABLE, THE REMAINDER SHALL CONTINUE IN FULL FORCE AND EFFECT, AND 
                      LIABILITY SHALL BE LIMITED TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-red-400/30">
                    <p className="text-red-300 font-bold text-center">
                      BY ACCEPTING THESE TERMS, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY 
                      THESE COMPREHENSIVE LIMITATIONS OF LIABILITY. IF YOU DO NOT AGREE, YOU MUST NOT USE THE PLATFORM.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 10: Termination */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                10. Account Termination
              </h2>
              <p className="mb-4">
                We reserve the right to terminate or suspend accounts at our discretion for violations of these 
                Terms, illegal activities, or other conduct harmful to the Platform or its users. Users may 
                terminate their accounts at any time by contacting customer support.
              </p>
              <p>
                Upon termination, certain provisions of these Terms will survive, including payment obligations, 
                limitation of liability, and intellectual property rights.
              </p>
            </section>

            {/* Section 11: Governing Law */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                11. Governing Law and Dispute Resolution
              </h2>
              <p className="mb-4">
                These Terms are governed by and construed in accordance with the laws of the United States and 
                the state of incorporation of D.C. Technology Corp., without regard to conflict of law principles.
              </p>
              <p>
                Any disputes arising from these Terms or Platform use shall be resolved through binding arbitration 
                in accordance with the rules of the American Arbitration Association, except for claims that may 
                be brought in small claims court.
              </p>
            </section>

            {/* Section 12: Contact Information */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4 border-b border-white/20 pb-2">
                12. Contact Information
              </h2>
              <div className="bg-blue-600/20 border border-blue-400/30 rounded-lg p-4">
                <p className="text-blue-200 mb-2">
                  <strong>For questions about these Terms and Conditions, please contact:</strong>
                </p>
                <div className="text-blue-200 text-sm space-y-1">
                  <p>D.C. Technology Corp.</p>
                  <p>Legal Department</p>
                  <p>Email: <a href="mailto:Corporate.dccorp@gmail.com" className="text-blue-300 underline">Corporate.dccorp@gmail.com</a></p>
                </div>
              </div>
            </section>

            {/* Effective Date */}
            <section className="border-t border-white/20 pt-6">
              <p className="text-center text-white/80 font-semibold">
                These Terms and Conditions are effective as of {new Date().toLocaleDateString()} and remain in 
                effect until modified or replaced by D.C. Technology Corp.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;